<template>
  <div>
    <KanbanSkeleton v-if="busy" />

    <!-- Show board selection and content if boards exist -->
    <template v-if="boardOptions.length > 0 && !busy">
      <b-container fluid>
        <!-- Board Selection Header -->
        <b-row class="mb-3 board-header-row">
          <b-col cols="12" class="d-flex align-items-center flex-wrap gap-2">
            <!-- Back button (only when board is selected) -->
            <b-button v-if="selectedBoard" variant="outline-secondary" size="sm" class="back-btn mr-2" @click="goToHome">
              <i class="fa fa-arrow-left mr-1"></i> All Boards
            </b-button>

            <!-- Board selector dropdown -->
            <div class="board-selector-wrapper">
              <multiselect v-model="selectedBoard" :options="boardOptions" :multiple="false"
                :taggable="true" :show-labels="false" label="text" track-by="value" placeholder="Select a Board">
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <div class="option__title d-flex">
                      <span class="option-code">{{props.option.text}}</span>
                    </div>
                  </div>
                </template>
              </multiselect>
            </div>

            <!-- "or" divider so users don't think they must pick an existing
                 board before creating a new one. Only shown when no board is
                 selected (dashboard state). -->
            <span v-if="!currentBoard" class="board-or-divider">or</span>

            <!-- Search box (only when board is selected). -->
            <div v-if="currentBoard" class="board-search-wrapper">
              <div class="board-search-box">
                <i class="fa fa-search search-icon"></i>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search tasks..."
                  class="board-search-input"
                  @input="onSearchInput"
                />
                <button v-if="searchQuery" class="search-clear-btn" @click="clearSearch">
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </div>

            <!-- New Board (sits next to the "or" divider on the dashboard;
                 sits before the delete button on the board view). -->
            <div class="board-actions d-flex align-items-center">
              <b-button @click="showNewBoardModal" variant="success" size="sm" class="no-wrap">
                <i class="fa fa-plus"></i> New Board
              </b-button>
              <b-button v-if="selectedBoard" variant="link" size="sm" class="manage-members-btn ml-1"
                  @click="openManageMembersModal" :title="currentBoardIsPrivate ? 'Manage members (private board)' : 'Manage members'">
                <i :class="currentBoardIsPrivate ? 'fa fa-lock' : 'fa fa-users'"></i>
              </b-button>
              <b-button v-if="selectedBoard" variant="link" size="sm" class="tag-alerts-btn ml-1"
                  @click="openTagAlertsModal" :title="currentBoardHasTagAlerts ? 'Tag alerts (on)' : 'Tag alerts'">
                <i class="fa fa-bell" :class="{ 'text-success': currentBoardHasTagAlerts }"></i>
              </b-button>
              <b-button v-if="selectedBoard" variant="link" size="sm" class="delete-board-btn ml-1" @click="showModal('sure-modal-remove-board')">
                <i class="fa fa-trash"></i>
              </b-button>
            </div>

            <!-- Kanban-wide notification settings (site-level, always visible).
                 Pushed to the far right via ml-auto to match the top-right
                 convention used by Chat/SMS/Tasks. -->
            <button type="button" class="kanban-settings-btn ml-auto" @click="openKanbanSettings" title="Kanban Settings">
              <i class="fa fa-cog"></i>
            </button>
          </b-col>
        </b-row>

        <!-- Conditional Content: Show board details or placeholder -->
        <template v-if="currentBoard">
          <!-- Board Title and Add Column Button Row -->
          <b-row class="mb-3">
            <b-col sm="8" md="8" class="d-flex align-items-center">
              <h2 class="h4 mb-0 board-title mr-4">{{ currentBoard ? currentBoard.name : '' }}</h2>
              <b-button v-if="currentBoard" @click="showAddColumnModal" variant="success" type="submit"
                class="btn-sm"><i class="fa fa-plus"></i> Add Column</b-button>
            </b-col>
          </b-row>

          <!-- Mobile View Dropdown -->
          <b-row class="d-md-none mb-3">
            <b-col>
              <b-form-select 
                v-model="selectedColumn" 
                :options="columnOptions" 
                @change="onColumnChange"
                class="mobile-column-select">
                <template #first>
                  <option :value="null" disabled>Select a column to view tasks</option>
                </template>
              </b-form-select>
            </b-col>
          </b-row>

          <!-- Search Results Indicator -->
          <div v-if="searchQuery" class="search-results-indicator">
            <span>
              <i class="fa fa-search mr-2"></i>
              Found {{ searchResultsCount }} task{{ searchResultsCount !== 1 ? 's' : '' }} matching "{{ searchQuery }}"
            </span>
            <span class="clear-link" @click="clearSearch">Clear search</span>
          </div>

          <!-- Kanban Board -->
          <b-row class="flex-fill" v-if="currentBoard">
            <!-- Desktop View: All columns side by side -->
            <b-col cols="12" class="d-none d-md-block kanban-board-container">
              <div class="d-flex overflow-auto kanban-container">
                <draggable
                  :list="currentBoard.columns"
                  :animation="200"
                  ghost-class="ghost-column"
                  drag-class="drag-column"
                  :group="{ name: 'columns', put: false }"
                  @change="handleColumnMove($event)"
                  :item-key="'column'"
                  class="d-flex column-drag-container"
                  handle=".column-drag-handle">
                  <b-card v-for="(column, index) in currentBoard.columns" :key="column.id" class="mr-3 kanban-column" no-body>
                  <b-card-body class="d-flex flex-column p-3">
                    <div class="d-flex justify-content-between align-items-center mb-3 column-header">
                      <div class="d-flex align-items-center flex-grow-1">
                        <span class="column-drag-handle mr-2 text-muted" title="Drag to reorder columns">
                          <span class="grip-dots"></span>
                        </span>
                        <b-card-title class="font-weight-semibold small text-secondary mb-0">
                          {{ column.name }}
                          <span class="ml-1 text-muted">({{ column.tasks.length }})</span>
                        </b-card-title>
                      </div>
                      <div class="column-actions">
                        <b-button variant="link" size="sm" class="p-1" @click="showAddTaskModal(column)">
                          <i class="fa fa-plus"></i>
                        </b-button>
                        <b-button variant="link" size="sm" class="p-1" @click="openEditColumnModal(column, index)">
                          <i class="fa fa-edit"></i>
                        </b-button>
                        <b-button variant="link" size="sm" class="p-1" @click="showModal('sure-modal-remove-column', index)">
                          <i class="fa fa-trash"></i>
                        </b-button>
                      </div>
                    </div>

                    <div class="column-tasks-container flex-fill">
                      <draggable 
                        :list="column.tasks" 
                        :animation="200" 
                        ghost-class="ghost-card"
                        drag-class="drag-item"
                        :group="{ name: 'tasks', put: true }" 
                        @change="handleTaskMove($event, column, index)" 
                        :item-key="column.id" 
                        class="drag-area-expanded"
                        :empty-insert-threshold="100">
                        <task-card
                          v-for="(task, taskIdx) in column.tasks"
                          :key="task.id"
                          :task="task"
                          :columnIdx="index"
                          :taskIdx="taskIdx"
                          :availableColumns="currentBoard.columns"
                          :currentColumnId="column.id"
                          :boardId="currentBoard.id"
                          @edit-task="handleTaskUpdate"
                          @remove-task="showRemoveTaskModal('sure-modal-remove-task', index, taskIdx)"
                          @move-task="handleMoveTask"
                          class="mb-3 cursor-grab"
                          :class="{ 'task-hidden': searchQuery && !taskMatchesSearch(task), 'task-highlight': searchQuery && taskMatchesSearch(task) }">
                        </task-card>
                      </draggable>
                      
                      <!-- Empty state for columns with no tasks - always visible when empty -->
                      <div v-if="column.tasks.length === 0" class="empty-column-state">
                        <i class="fa fa-tasks text-muted mb-2"></i>
                        <p class="text-muted small mb-0">Drop tasks here</p>
                      </div>
                    </div>
                  </b-card-body>
                </b-card>
                </draggable>
              </div>
            </b-col>

            <!-- Mobile View: Single column based on selection -->
            <b-col cols="12" class="d-md-none mobile-kanban-container">
              <b-card v-if="selectedColumn" class="mobile-column-card h-100" no-body>
                <b-card-body class="p-3 d-flex flex-column h-100">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <b-card-title class="font-weight-semibold mobile-column-title mb-0">
                      {{ selectedColumn.name }}
                      <span class="ml-2 task-count-badge">({{ selectedColumn.tasks.length }})</span>
                    </b-card-title>
                    <div class="mobile-column-actions">
                      <b-button variant="success" size="sm" @click="showAddTaskModal(selectedColumn)" class="mobile-add-btn">
                        <i class="fa fa-plus mr-1"></i>Add Task
                      </b-button>
                    </div>
                  </div>

                  <div class="mobile-tasks-container flex-fill">
                    <draggable 
                      :list="selectedColumn.tasks" 
                      :animation="200" 
                      ghost-class="ghost-card" 
                      group="tasks"
                      @change="handleTaskMove($event, selectedColumn, currentBoard.columns.findIndex(col => col.id === selectedColumn.id))"
                      class="h-100">
                      <task-card
                        v-for="(task, taskIdx) in selectedColumn.tasks"
                        :key="task.id"
                        :task="task"
                        :columnIdx="currentBoard.columns.findIndex(col => col.id === selectedColumn.id)"
                        :taskIdx="taskIdx"
                        :availableColumns="currentBoard.columns"
                        :currentColumnId="selectedColumn.id"
                        :boardId="currentBoard.id"
                        @edit-task="handleTaskUpdate"
                        @remove-task="showRemoveTaskModal('sure-modal-remove-task', currentBoard.columns.findIndex(col => col.id === selectedColumn.id), taskIdx)"
                        @move-task="handleMoveTask"
                        class="mb-3 cursor-grab"
                        :class="{ 'task-hidden': searchQuery && !taskMatchesSearch(task), 'task-highlight': searchQuery && taskMatchesSearch(task) }">
                      </task-card>
                    </draggable>
                    
                    <div v-if="selectedColumn.tasks.length === 0" class="empty-column-mobile">
                      <i class="fa fa-tasks text-muted mb-2" style="font-size: 2rem;"></i>
                      <p class="text-muted mb-2">No tasks in this column</p>
                      <b-button variant="outline-success" size="sm" @click="showAddTaskModal(selectedColumn)">
                        <i class="fa fa-plus mr-1"></i>Add First Task
                      </b-button>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
              
              <div v-else class="mobile-no-column-selected h-100">
                <div class="text-center py-5 h-100 d-flex flex-column justify-content-center">
                  <i class="fa fa-arrow-up text-muted mb-3" style="font-size: 2rem;"></i>
                  <h5 class="text-muted">Select a column above to view tasks</h5>
                  <p class="text-muted small">Use the dropdown to switch between columns</p>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <!-- Centered Dashboard when boards exist but none is selected -->
          <div class="kanban-dashboard-wrapper">
            <div class="kanban-dashboard">
              <!-- Hero Section with Illustration -->
              <div class="kanban-hero">
                <KanbanPreviewIllustration :showCaption="false" />
                <h3 class="kanban-hero-title">Select a board to get started</h3>
                <p class="kanban-hero-subtitle">Choose from your recent boards below or create a new one</p>
              </div>

              <!-- Recent Boards - Compact List -->
              <div class="recent-boards-section">
                <h6 class="section-title mb-3">
                  <i class="fa fa-clock-o mr-2"></i>Recent Boards
                </h6>

                <!-- Compact Board List -->
                <div class="board-list">
                  <div
                    class="board-list-item"
                    v-for="board in visibleBoards"
                    :key="board.id"
                    @click="selectBoard(board)"
                  >
                    <div class="board-list-icon">
                      <i class="fa fa-th-large"></i>
                    </div>
                    <div class="board-list-content">
                      <h6 class="board-list-title">
                        <i
                          v-if="isBoardPrivate(board)"
                          class="fa fa-lock board-list-lock mr-1"
                          title="Private board — only its members can see it"
                          data-test="board-list-private-lock"
                        ></i>{{ board.name }}
                      </h6>
                      <span class="board-list-meta">
                        {{ getBoardTaskCount(board) }} tasks · {{ board.columns ? board.columns.length : 0 }} columns · {{ formatDate(board.updatedAt || board.lastModified || board.createdAt) }}
                      </span>
                    </div>
                    <!-- Delete button on each row. Was a b-dropdown earlier
                         but 5+ b-dropdown instances on the dashboard appeared
                         to cause page-freeze issues — reverted to a plain icon
                         button. -->
                    <button
                      type="button"
                      class="board-list-delete-btn"
                      title="Delete this board"
                      @click.stop="confirmDeleteBoardFromList(board)"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>

                  <!-- Show all / fewer toggle: the top-5 "recent" cut hides any
                       board without recent activity, so every board must stay
                       reachable from the dashboard, not just via the dropdown. -->
                  <div
                    v-if="sortedBoards.length > 5"
                    class="board-list-item board-list-toggle"
                    data-test="board-list-show-all-toggle"
                    @click="showAllBoards = !showAllBoards"
                  >
                    <div class="board-list-icon">
                      <i :class="showAllBoards ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                    </div>
                    <div class="board-list-content">
                      <h6 class="board-list-title">
                        {{ showAllBoards ? 'Show fewer boards' : `Show all ${sortedBoards.length} boards` }}
                      </h6>
                    </div>
                  </div>

                  <!-- Create New Board Row -->
                  <div class="board-list-item board-list-new" @click="showNewBoardModal">
                    <div class="board-list-icon new">
                      <i class="fa fa-plus"></i>
                    </div>
                    <div class="board-list-content">
                      <h6 class="board-list-title">Create New Board</h6>
                      <span class="board-list-meta">Start from scratch or use a template</span>
                    </div>
                    <div class="board-list-arrow">
                      <i class="fa fa-chevron-right"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Add Task is now a slide-out panel (AddTaskPanel.vue) invoked via
             $showPanel in showAddTaskModal(). Kept outside b-modal so the
             <at> picker's caret-positioning math works correctly (b-modal's
             position:fixed ancestor breaks getOffset()). -->

        <!-- Add Column Modal -->
        <b-modal ref="addColumnModal" v-model="showColumnModal" title="Add New Column" body-class="bg-white mb-0">
          <b-form>
            <b-form-group label="Column Name">
              <b-form-input v-model="columnName" required placeholder="Enter column Name"></b-form-input>
            </b-form-group>
          </b-form>
          <div slot="modal-footer" class="w-100">
            <b-button variant="success" size="sm" class="float-right" @click="handleAddColumn">Add Column</b-button>
            <b-button variant="danger" size="sm" class="float-right mr-3" @click="hideModal('addColumnModal')">Cancel</b-button>          
          </div>
        </b-modal>
        <!-- Edit Column Modal -->
        <b-modal ref="editColumnModal" v-model="showEditColumnModal" title="Edit Column" body-class="bg-white mb-0">
          <b-form>
            <b-form-group label="Column Name">
              <b-form-input v-model="editColumnName" required placeholder="Enter column Name"></b-form-input>
            </b-form-group>
          </b-form>
          <div slot="modal-footer" class="w-100">
            <b-button variant="success" size="sm" class="float-right" @click="handleEditColumn">Save Changes</b-button>
            <b-button variant="danger" size="sm" class="float-right mr-3" @click="hideModal('editColumnModal')">Cancel</b-button>          
          </div>
        </b-modal>
        <!-- Delete Board Modal -->
        <b-modal ref="sure-modal-remove-board" size="sm" body-class="bg-white" title="Are you sure?"
          @ok="runSureOk('remove-board')" @cancel="hideModal('sure-modal-remove-board')">
          Are you sure you want to delete the board <b>{{ currentBoard ? currentBoard.name : '' }}</b>?
        </b-modal>

        <!-- Manage Members Modal (board visibility). Reuses the same picker
             pattern as the Create Board modal. Public = visible to everyone
             at the site. Private = only the listed employees (current user
             is auto-included). Firestore rules enforce the same predicate
             server-side. -->
        <b-modal ref="manageMembersModal" v-model="showManageMembersModal" title="Manage Board Members" body-class="bg-white" size="md">
          <div v-if="currentBoard">
            <p class="text-muted small mb-3">
              Editing visibility for <b>{{ currentBoard.name }}</b>.
            </p>
            <label class="form-label mb-2">Visibility</label>
            <b-form-radio-group v-model="manageMembersIsPrivate" :options="visibilityOptions" stacked size="sm"></b-form-radio-group>
            <div v-if="manageMembersIsPrivate" class="mt-3">
              <small class="text-muted d-block mb-1">
                Only these employees will see this board. You're automatically included.
              </small>
              <multiselect
                v-model="manageMembersList"
                :options="otherActiveEmployees"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Add employees..."
                label="Name"
                track-by="EmployeeID">
              </multiselect>
            </div>
          </div>
          <div slot="modal-footer" class="w-100">
            <b-button variant="success" size="sm" class="float-right" @click="saveBoardMembership">Save</b-button>
            <b-button variant="danger"  size="sm" class="float-right mr-3" @click="hideModal('manageMembersModal')">Cancel</b-button>
          </div>
        </b-modal>

        <!-- Tag Alerts Modal (board-level). "When a card gets one of these tags,
             these people get an email." Off by default. Sits next to Manage
             Members and mirrors its picker patterns. PRIVACY: on a PRIVATE board
             only board members are selectable recipients (a non-member can't see
             the board, so emailing them would leak the card); on a public board
             any active site employee is eligible. -->
        <b-modal ref="tagAlertsModal" v-model="showTagAlertsModal" title="Tag Alerts" body-class="bg-white" size="md">
          <div v-if="currentBoard">
            <!-- EMAIL-CAPABILITY GATE: tag alerts send email, so the settings
                 form only renders when the site can actually send email
                 (derived from a verified SendGrid sender). Otherwise show the
                 standard upsell. -->
            <template v-if="emailEnabled">
              <p class="text-muted small mb-3">
                Editing tag alerts for <b>{{ currentBoard.name }}</b>.
              </p>
              <b-form-checkbox v-model="tagAlertsEnabled" switch class="mb-2 tag-alerts-toggle">
                Email alerts for tagged cards
              </b-form-checkbox>
              <div v-if="tagAlertsEnabled" class="mt-3">
                <label class="form-label mb-1 small text-muted d-block">Tags that trigger an alert</label>
                <multiselect
                  v-model="tagAlertsTags"
                  :options="[]"
                  track-by="name"
                  label="name"
                  :multiple="true"
                  :taggable="true"
                  tag-placeholder="Press Enter to add as a new tag"
                  placeholder="Type a tag and press Enter..."
                  @tag="onAddAlertTag"
                  @search-change="onAlertTagSearchChange"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="false"
                  :preselect-first="false"
                  :hide-selected="true">
                </multiselect>
                <small class="text-muted d-block mt-1">Up to 10 tags, 40 characters each.</small>

                <label class="form-label mb-1 mt-3 small text-muted d-block">People to email</label>
                <multiselect
                  v-model="tagAlertsRecipients"
                  :options="tagAlertRecipientOptions"
                  :multiple="true"
                  :max="20"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Add people..."
                  label="Name"
                  track-by="EmployeeID">
                </multiselect>
                <small class="text-muted d-block mt-1">Up to 20 people.</small>
                <small class="text-muted d-block mt-2">
                  When a card gets one of these tags, these people get an email.
                </small>
              </div>
            </template>

            <FeaturePreview
              v-else
              title="Email Alerts for Tagged Cards"
              description="Sign up for email credits with Teamio and we'll notify your team by email the moment a card gets an important tag."
              icon="fa fa-bell"
              feature-key="EMAIL"
              feature-label="Patient email"
              source-page="Kanban - Email alerts for tagged cards">
              <template #preview>
                <!-- NOTE ON STYLING: the .list-item / .item-avatar / .item-* classes
                     below are styled by FeaturePreview.vue's OWN scoped block (its
                     `.preview-items .list-item { … }` family) — not by anything on this
                     page and not by AdminPageModern.scss. Vue 2 stamps slot content with
                     the HOST component's scope id as well as the parent's, so
                     FeaturePreview's scoped rules reach markup we pass into its `preview`
                     slot. That is why this renders styled with no rules here.

                     Corrected 2026-09-02. An earlier version of this comment blamed the
                     unscoped AdminPageModern.scss; that was wrong — every .list-item rule
                     in that sheet is nested under `.dashboard-page`, a wrapper this page
                     does not have, so those rules can never match here (verified against
                     the compiled prod CSS). Consequence: this preview is ALREADY safe
                     under route-level code splitting, because FeaturePreview's CSS travels
                     with FeaturePreview.

                     What would break it: deleting or narrowing the `.list-item` family in
                     src/components/FeaturePreview/FeaturePreview.vue. Pinned by
                     cypress/node-tests/feature-preview-slot-styles.test.js. -->
                <div class="tag-alert-upsell-preview">
                  <div class="list-item">
                    <div class="item-avatar form-avatar"><i class="fa fa-bell"></i></div>
                    <div class="item-content">
                      <div class="item-header">
                        <span class="item-title">"Broken chair" card tagged Urgent</span>
                        <span class="item-time">just now</span>
                      </div>
                      <p class="item-preview">Email sent to Tanya, Brandi — a card on Front Desk got the "urgent" tag.</p>
                    </div>
                  </div>
                </div>
              </template>
            </FeaturePreview>
          </div>
          <div slot="modal-footer" class="w-100">
            <b-button v-if="emailEnabled" variant="success" size="sm" class="float-right" @click="saveTagAlerts">Save</b-button>
            <b-button variant="danger"  size="sm" class="float-right mr-3" @click="hideModal('tagAlertsModal')">{{ emailEnabled ? 'Cancel' : 'Close' }}</b-button>
          </div>
        </b-modal>
        <!-- Delete Column Modal -->
        <b-modal ref="sure-modal-remove-column" size="sm" body-class="bg-white" title="Are you sure?"
          @ok="runSureOk('remove-column')" @cancel="hideModal('sure-modal-remove-column')">
          <div v-if="getColumnToDelete && getColumnToDelete.tasks.length > 0">
            <p class="mb-2">
              <strong>Warning:</strong> This column contains {{ getColumnToDelete.tasks.length }} task{{ getColumnToDelete.tasks.length > 1 ? 's' : '' }}.
            </p>
            <p class="mb-0">
              Deleting this column will permanently remove all tasks inside it. This action cannot be undone.
            </p>
          </div>
          <div v-else>
            Are you sure you want to delete this column?
          </div>
        </b-modal>
        <!-- Delete Task Modal -->
        <b-modal ref="sure-modal-remove-task" size="sm" body-class="bg-white" title="Are you sure?"
          @ok="runSureOk('remove-task')" @cancel="hideModal('sure-modal-remove-task')">
          Are you sure you want to delete this card?
        </b-modal>
      </b-container>
    </template>
    
    <!-- Show empty state if no boards exist -->
    <template v-else>
      <b-container class="kanban-empty-container">
        <div class="kanban-empty-state">
          <!-- Explainer Section -->
          <div class="kanban-explainer">
            <h3 class="explainer-title">
              <i class="fa fa-th-large"></i>
              Organize Tasks Visually
            </h3>
            <p class="explainer-description">
              Kanban boards help you organize tasks into <strong>columns</strong> that represent different stages of your workflow.
              Drag and drop tasks to track progress and keep your team aligned!
            </p>
            <div class="explainer-examples">
              <div class="example-item">
                <i class="fa fa-columns"></i>
                <span>Create custom columns like "To Do", "In Progress", "Done"</span>
              </div>
              <div class="example-item">
                <i class="fa fa-arrows"></i>
                <span>Drag tasks between columns as work progresses</span>
              </div>
              <div class="example-item">
                <i class="fa fa-tags"></i>
                <span>Add tags, due dates, and assignees to tasks</span>
              </div>
            </div>
          </div>

          <!-- Visual Illustration -->
          <KanbanPreviewIllustration :showCaption="false" />

          <b-button @click="showNewBoardModal" variant="success" class="mt-4 create-board-btn">
            <i class="fa fa-plus mr-2"></i> Create Your First Board
          </b-button>
        </div>
      </b-container>
    </template>

    <!-- New Board Modal (Accessible from both states) -->
    <b-modal ref="createBoardModal" v-model="showBoardModal" title="Create New Board" body-class="bg-white mb-0" size="lg">
        <!-- Quick Start Templates -->
        <div class="templates-in-modal mb-4">
          <label class="form-label mb-2">Quick Start from Template</label>
          <div class="template-grid">
            <div
              v-for="template in boardTemplates"
              :key="template.id"
              class="template-option"
              :class="{ 'selected': selectedTemplate === template.id }"
              @click="selectTemplate(template)"
            >
              <div class="template-option-icon" :style="{ backgroundColor: template.color + '15' }">
                <i :class="template.icon" :style="{ color: template.color }"></i>
              </div>
              <div class="template-option-content">
                <span class="template-option-name">{{ template.name }}</span>
                <span class="template-option-cols">{{ template.columns.length }} columns</span>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-3">

        <!-- Or Create Custom -->
        <div class="custom-board-section">
          <label class="form-label mb-2">Or Create Custom Board</label>
          <b-form>
            <b-form-group label="Board Name" label-class="small text-muted">
              <b-form-input v-model="boardName" required placeholder="Enter board name" @input="selectedTemplate = null"></b-form-input>
            </b-form-group>
            <b-form-group label="Description" label-class="small text-muted">
              <b-form-textarea v-model="boardDescription" placeholder="Enter board description (optional)"
                rows="2"></b-form-textarea>
            </b-form-group>
          </b-form>
        </div>

        <hr class="my-3">

        <!-- Visibility: public (everyone at the site) OR restricted to a chosen
             set of employees. Default is public to match existing behavior. -->
        <div class="visibility-section">
          <label class="form-label mb-2">Visibility</label>
          <b-form-radio-group v-model="newBoardIsPrivate" :options="visibilityOptions" stacked size="sm"></b-form-radio-group>
          <div v-if="newBoardIsPrivate" class="mt-2">
            <small class="text-muted d-block mb-1">
              Only these employees will see this board. You're automatically included.
            </small>
            <multiselect
              v-model="newBoardMembers"
              :options="otherActiveEmployees"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              placeholder="Add employees..."
              label="Name"
              track-by="EmployeeID">
            </multiselect>
          </div>
        </div>

        <div slot="modal-footer" class="w-100">
          <b-button variant="success" size="sm" class="float-right" @click="handleCreateBoardOrTemplate">Create Board</b-button>
          <b-button variant="danger" size="sm" class="float-right mr-3" @click="hideModal('createBoardModal')">Cancel</b-button>
        </div>
      </b-modal>

    <!-- Slide-out panel mount (required by vue2-slideout-panel). Placed at
         the END of the template to match AptReminder / AdminTable /
         ConversationList. Placement at the TOP was breaking the main page's
         click handling. -->
    <slideout-panel></slideout-panel>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import TaskCard from './TaskCard.vue';
import KanbanPreviewIllustration from './components/KanbanPreviewIllustration.vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import mixin from '../../mixin.js';
import moment from 'moment-timezone';
import Multiselect from "vue-multiselect";
import KanbanSkeleton from './KanbanSkeleton.vue';
import _ from 'lodash';
import Vue from 'vue';
import KanbanSettings from './components/KanbanSettings.vue';
import AddTaskPanel from './components/AddTaskPanel.vue';
import { boardSortKey } from '@/utils/kanbanBoards';
import { normalizeTagNames } from '@/utils/kanbanTagAlerts';
import { isEmailEnabled } from '@/utils/siteCapability';
import FeaturePreview from '@/components/FeaturePreview';

export default {
  name: 'Kanban',

  components: {
    draggable,
    TaskCard,
    KanbanPreviewIllustration,
    Multiselect,
    KanbanSkeleton,
    FeaturePreview
  },

  mixins: [mixin],

  data() {
    return {
      busy: true,  // Start with busy as true by default
      boards: [],
      boardName: '',
      boardDescription: '',
      selectedBoard: null,
      selectedBoardId: null,
      currentBoard: null,
      selectedColumn: null,
      selectedColumnIdx: null,
      selectedTaskIdx: null,
      showColumnModal: false,
      showEditColumnModal: false,
      showBoardModal: false,
      showAllBoards: false,
      columnName: '',
      editColumnName: '',
      editColumnIndex: null,
      newBoard: {
          name: '',
          description: ''
      },
      unsubscribeBoard: null,
      unsubscribeBoards: null,
      selectedTemplate: null,
      searchQuery: '',
      searchDebounceTimer: null,
      // Board visibility picker state for the create modal.
      // newBoardIsPrivate=false → board is created public (accessibleBy: ['*']).
      // newBoardIsPrivate=true  → board is created private, with the creator
      // plus everyone in newBoardMembers as members.
      newBoardIsPrivate: false,
      newBoardMembers: [],
      visibilityOptions: [
        { value: false, text: 'Visible to everyone at the site' },
        { value: true,  text: 'Restrict to specific employees' },
      ],
      // Manage Members modal state (separate from create-modal state so the
      // two modals don't share fields and step on each other).
      showManageMembersModal: false,
      manageMembersIsPrivate: false,
      manageMembersList: [],
      // Tag Alerts modal state. tagAlertsTags is [{name}] (vue-multiselect
      // shape); tagAlertsRecipients is [{EmployeeID, Name, ...}]. Normalized +
      // capped on save (see saveTagAlerts).
      showTagAlertsModal: false,
      tagAlertsEnabled: false,
      tagAlertsTags: [],
      tagAlertsRecipients: [],
      // Text typed into the alert-tags multiselect but NOT yet committed with
      // Enter. vue-multiselect keeps it in its own internal search state, so a
      // user who types "urgent" and clicks Save would otherwise have it silently
      // discarded — that shipped an enabled-but-inert config to the first office
      // that used the feature (Allay, 2026-08-19). saveTagAlerts commits it.
      tagAlertsPendingTag: '',
      // Board templates for quick start
      boardTemplates: [
        {
          id: 'project',
          name: 'Project Management',
          description: 'Track project tasks from start to finish',
          icon: 'fa fa-briefcase',
          color: '#217CE8',
          columns: ['Backlog', 'To Do', 'In Progress', 'Review', 'Done']
        },
        {
          id: 'sprint',
          name: 'Sprint Planning',
          description: 'Agile sprint board for dev teams',
          icon: 'fa fa-bolt',
          color: '#28a745',
          columns: ['Sprint Backlog', 'In Development', 'Testing', 'Done']
        },
        {
          id: 'sales',
          name: 'Sales Pipeline',
          description: 'Track leads through your sales funnel',
          icon: 'fa fa-dollar',
          color: '#ffc107',
          columns: ['Leads', 'Contacted', 'Proposal', 'Negotiation', 'Closed']
        },
        {
          id: 'personal',
          name: 'Personal Tasks',
          description: 'Simple board for personal productivity',
          icon: 'fa fa-user',
          color: '#6f42c1',
          columns: ['To Do', 'Doing', 'Done']
        }
      ]
    }
  },

  mounted() {
    // Force a DOM repaint
    this.$nextTick(() => {
      this.loadBoards();
    });
  },

  computed: {        
      ...mapState({
        fbboards: state => state.kanban.boards,          
      }),

      ...mapGetters({
        'getBoard' : 'kanban/getBoard',        
      }),
      
      boardOptions() {
        if(this.fbboards && this.fbboards.length > 0) {
          return this.fbboards.map(board => ({
            value: board.id,
            text: board.name
          }))
        } else return [];
      },

      // Active employees at the current site. Used by the visibility picker
      // in the create-board modal and (later) the board-settings modal.
      // Mirrors the pattern in AddTaskPanel.vue::activeEmployees.
      activeEmployees() {
        const list = this.$store.state.customer.employees;
        if (!list) return [];
        try {
          return list.filter(e => e.Active === 1 || e.Active === '1');
        } catch (_) {
          return [];
        }
      },

      // Pickable members for a new private board: everyone except the creator
      // (the creator is auto-included server-side via createBoard).
      otherActiveEmployees() {
        const me = this.userID;
        return this.activeEmployees.filter(e => e.EmployeeID !== me);
      },

      // True if the current board is private (has an explicit member list,
      // not the public wildcard). Used to swap the manage-members icon.
      currentBoardIsPrivate() {
        return this.isBoardPrivate(this.currentBoard);
      },

      // True when the current board has tag alerts turned on — highlights the
      // bell icon so staff can see at a glance the board is wired up.
      currentBoardHasTagAlerts() {
        return !!(this.currentBoard && this.currentBoard.tagAlerts && this.currentBoard.tagAlerts.enabled);
      },

      // Tag alerts are an EMAIL feature — gate the settings form on the site's
      // derived email capability (verified SendGrid sender). When absent, the
      // modal shows the FeaturePreview upsell instead of the form. Uses the
      // canonical helper (src/utils/siteCapability.js) — never an inline test.
      // `settings`/`allSettings` come from the global mixin.
      emailEnabled() {
        return isEmailEnabled(this.allSettings);
      },

      // Selectable recipients for the tag-alerts picker. PRIVACY: on a private
      // board only board members can be emailed (a non-member can't see the
      // board, so alerting them would leak the card); on a public board any
      // active employee is eligible. Same accessibleBy predicate the @-mention
      // pickers use (AddTaskPanel/EditTaskPanel activeEmployees).
      tagAlertRecipientOptions() {
        const all = this.activeEmployees;
        const acc = this.currentBoard && this.currentBoard.accessibleBy;
        // A board is PUBLIC only when accessibleBy is EXACTLY ['*'] — the same test
        // the backend authority applies (main-service/kanban-alerts.js::
        // resolveRecipientIDs). Anything else — including a MISSING or EMPTY
        // accessibleBy — is private, and the backend intersects recipients with it,
        // so a board with no member list can reach nobody.
        //
        // This used to return every employee when accessibleBy was missing, which
        // disagreed with the backend: the picker offered people the backend would
        // silently discard, producing another alert that saves fine and never sends.
        // Fail CLOSED instead (the privacy-safe direction too — never widen an
        // unknown audience); saveTagAlerts' inert-config guard then says so plainly.
        if (!Array.isArray(acc)) return [];
        if (acc.length === 1 && acc[0] === '*') return all;
        return all.filter(e => acc.includes(e.EmployeeID));
      },

      // NOTE: activeEmployees moved to the panel components — Kanban.vue no
      // longer renders <at> directly. AddTaskPanel.vue + EditTaskPanel.vue
      // each compute their own activeEmployees from the store.

      columnOptions() {
        if(this.currentBoard && this.currentBoard.columns && this.currentBoard.columns.length > 0) {
          return this.currentBoard.columns.map(column => ({
            value: column,
            text: `${column.name} (${column.tasks.length})`
          }))
        }
        return [];
      },

      getColumnToDelete() {
        if (this.currentBoard && this.currentBoard.columns && this.selectedColumnIdx !== null) {
          return this.currentBoard.columns[this.selectedColumnIdx];
        }
        return null;
      },

      // Filtered columns based on search
      filteredColumns() {
        if (!this.currentBoard || !this.currentBoard.columns) return [];
        if (!this.searchQuery) return this.currentBoard.columns;

        // Filter tasks within each column
        return this.currentBoard.columns.map(column => ({
          ...column,
          tasks: column.tasks.filter(task => this.taskMatchesSearch(task))
        }));
      },

      searchResultsCount() {
        if (!this.searchQuery || !this.filteredColumns) return 0;
        return this.filteredColumns.reduce((total, col) => total + col.tasks.length, 0);
      },

      // Dashboard computed properties. Sort key shared with the Admin
      // KanbanWidget — see src/utils/kanbanBoards.js for the fallback chain
      // and the Tanya/Allay 2026-06-11 background.
      sortedBoards() {
        if (!this.fbboards || this.fbboards.length === 0) return [];
        return [...this.fbboards]
          .sort((a, b) => boardSortKey(b) - boardSortKey(a));
      },

      recentBoards() {
        return this.sortedBoards.slice(0, 5);
      },

      // What the dashboard list actually renders: top 5, or everything when
      // the user expands "Show all boards".
      visibleBoards() {
        return this.showAllBoards ? this.sortedBoards : this.recentBoards;
      }
  },

  methods: {
    ...mapActions('kanban', ['createBoard', 'addColumn', 'addTask', 'updateBoard', 'updateBoardMembership', 'updateTagAlerts']),

    // Dashboard helper methods
    getBoardTaskCount(board) {
      if (!board.columns) return 0;
      return board.columns.reduce((total, col) => total + (col.tasks ? col.tasks.length : 0), 0);
    },

    // Shared private-board test: a board is private when it has an explicit
    // accessibleBy member list that isn't the public wildcard ['*']. Used both
    // for the current-board manage-members icon and the recent-boards lock.
    isBoardPrivate(board) {
      const acc = board && board.accessibleBy;
      if (!Array.isArray(acc) || acc.length === 0) return false;
      return !(acc.length === 1 && acc[0] === '*');
    },

    getPreviewColumns(board) {
      if (!board.columns) return [];
      return board.columns.slice(0, 3);
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    formatDate(date) {
      if (!date) return 'Recently';
      const d = date?.toDate?.() || new Date(date);
      return moment(d).fromNow();
    },

    selectBoard(board) {
      Vue.set(this, 'selectedBoard', { text: board.name, value: board.id });
      Vue.set(this, 'selectedBoardId', board.id);
      Vue.set(this, 'currentBoard', board);
      this.searchQuery = '';
    },

    goToHome() {
      Vue.set(this, 'selectedBoard', null);
      this.selectedBoardId = null;
      this.currentBoard = null;
      this.selectedColumn = null;
      this.searchQuery = '';
    },

    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        // Search is reactive, no need to do anything here
      }, 300);
    },

    clearSearch() {
      this.searchQuery = '';
    },

    taskMatchesSearch(task) {
      if (!this.searchQuery) return true;
      const query = this.searchQuery.toLowerCase();
      const nameMatch = task.name && task.name.toLowerCase().includes(query);
      const detailsMatch = task.details && task.details.toLowerCase().includes(query);
      const typeMatch = task.type && task.type.toLowerCase().includes(query);
      const tagsMatch = task.tags && task.tags.some(tag => tag.name && tag.name.toLowerCase().includes(query));
      return nameMatch || detailsMatch || typeMatch || tagsMatch;
    },

    selectTemplate(template) {
      this.selectedTemplate = template.id;
      this.boardName = template.name;
      this.boardDescription = template.description;
    },

    async handleCreateBoardOrTemplate() {
      // If a template is selected, use it
      if (this.selectedTemplate) {
        const template = this.boardTemplates.find(t => t.id === this.selectedTemplate);
        if (template) {
          await this.createFromTemplate(template);
          return;
        }
      }
      // Otherwise create custom board
      await this.handleCreateBoard();
    },

    async createFromTemplate(template) {
      try {
        const columns = template.columns.map((colName, idx) => ({
          id: (Date.now() + idx).toString(),
          name: colName,
          tasks: []
        }));

        let payload = {
          siteID: this.siteID,
          name: this.boardName || template.name,
          description: this.boardDescription || template.description,
          columns: columns,
          memberEmployeeIDs: this.newBoardIsPrivate ? this.newBoardMembers.map(e => e.EmployeeID) : [],
        };

        let board = await this.createBoard(payload);
        Vue.set(this, 'selectedBoard', { text: board.name, value: board.id });
        Vue.set(this, 'selectedBoardId', board.id);
        Vue.set(this, 'currentBoard', board);

        this.$toast.success(`${board.name} board created!`);
        this.resetBoardModalFields();
        this.showBoardModal = false;
      } catch (error) {
        this.$toast.error('Error creating board from template');
        console.error(error);
      }
    },

    async handleCreateBoard() { 
      if (this.boardName) { 
        try {
          // Create default columns for the skeleton board
          const defaultColumns = [
            { id: Date.now().toString(), name: 'To Do', tasks: [] },
            { id: (Date.now() + 1).toString(), name: 'In Progress', tasks: [] },
            { id: (Date.now() + 2).toString(), name: 'In Review', tasks: [] },
            { id: (Date.now() + 3).toString(), name: 'Done', tasks: [] }
          ];
          
          let payload = {
            siteID: this.siteID,
            name: this.boardName,
            description: this.boardDescription,
            columns: defaultColumns,
            memberEmployeeIDs: this.newBoardIsPrivate ? this.newBoardMembers.map(e => e.EmployeeID) : [],
          }
          let board = await this.createBoard(payload);
          Vue.set(this, 'selectedBoard', {text: board.name, value: board.id});
          Vue.set(this, 'selectedBoardId', board.id);
          Vue.set(this, 'currentBoard', board);

          // Show success message about skeleton board creation
          this.$toast.success("New board created with default columns!");

          this.resetBoardModalFields();
          this.showBoardModal = false;
        } catch (error) {
          this.$toast.error("Error creating board. Please try again");
          this.$toast.error(error.message);
        }          
      } 
    },

    // NOTE: addTag moved to AddTaskPanel.vue (tags live on the panel's form now).

    showNewBoardModal() {
      this.showBoardModal = true;
    },

    onBoardChange(board) {
    },

    onColumnChange(column) {
      this.selectedColumn = column;
    },

    // Open the Add Task slide-out panel. Moved from b-modal to $showPanel
    // because <at>'s caret positioning math breaks inside position:fixed
    // modal ancestors. Slide-out panels don't break it (proven by
    // TodoDetails.vue in Tasks).
    //
    // The panel OWNS the save (dispatches kanban/addTask internally) so the
    // parent's .promise callback doesn't race with the plugin's close
    // transition. Previous design had the parent dispatch after emit —
    // that re-rendered Kanban.vue mid-transition and left the backdrop
    // stuck, freezing the page.
    showAddTaskModal(column) {
      this.selectedColumn = column;
      this.$showPanel({
        component: AddTaskPanel,
        openOn: 'right',
        width: 720,
        disableBgClick: true,
        disableEscClick: true,
        props: {
          boardId: this.currentBoard.id,
          columnId: column.id,
        },
      });
    },

    openKanbanSettings() {
      const handle = this.$showPanel({
        component: KanbanSettings,
        openOn: 'right',
        width: 720,
        disableBgClick: true,
        disableEscClick: true,
      });
      if (handle && handle.promise) {
        handle.promise.then(() => { /* panel closed */ });
      }
    },

    async handleTaskUpdate(updatedTask, columnIdx, taskIdx, extras) {
      if (!updatedTask.name) {
        this.$toast.error("Missing Task Name. Please try again");
        return false;
      }

      try {
        // Update the task in the current board
        const updatedColumns = [...this.currentBoard.columns];
        updatedColumns[columnIdx].tasks[taskIdx] = updatedTask;

        // Prepare the payload to update the board
        const payload = {
          boardId: this.currentBoard.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        // Dispatch the update action
        await this.$store.dispatch('kanban/updateBoard', payload);

        // Write @-mention notifications for NEWLY-added assignees (TaskCard
        // already diffed and excluded self). Failure to write shouldn't block
        // the "task updated" success message.
        if (extras && Array.isArray(extras.notification) && extras.notification.length > 0) {
          const boardId = this.currentBoard.id;
          for (const n of extras.notification) {
            try {
              n.boardId = boardId;
              await this.$store.dispatch('kanban/writeKanbanNotification', n);
            } catch (e) {
              try { console.warn('[kanban] Failed to write edit notification:', e); } catch(_) { /* ignore */ }
            }
          }
        }

        this.$toast.success('Task updated successfully!');

      } catch (error) {
        this.$toast.error("Error updating task. Please try again.");
      }
    },

    showModal(modal, selectedColumnIdx) {
      this.selectedColumnIdx = selectedColumnIdx;        
      this.$refs[modal].show();
    },

    showRemoveTaskModal(modal, columnIdx, taskIdx) {
      this.selectedColumnIdx = columnIdx;
      this.selectedTaskIdx = taskIdx;        
      this.$refs[modal].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide();
      // Reset visibility picker state when the create-board modal is cancelled
      // so the next open starts fresh (default: public, no members).
      if (ref === 'createBoardModal') {
        this.resetBoardModalFields();
      }
      if (ref === 'manageMembersModal') {
        this.manageMembersIsPrivate = false;
        this.manageMembersList = [];
      }
      if (ref === 'tagAlertsModal') {
        this.tagAlertsEnabled = false;
        this.tagAlertsTags = [];
        this.tagAlertsRecipients = [];
        this.tagAlertsPendingTag = '';
      }
    },

    // Open the manage-members modal pre-filled with the current board's
    // visibility state.
    openManageMembersModal() {
      if (!this.currentBoard) return;
      const acc = Array.isArray(this.currentBoard.accessibleBy) ? this.currentBoard.accessibleBy : [];
      const isPrivate = acc.length > 0 && !(acc.length === 1 && acc[0] === '*');
      this.manageMembersIsPrivate = isPrivate;
      if (isPrivate) {
        const me = this.userID;
        const memberIDs = acc.filter(id => id !== me && id !== '*');
        this.manageMembersList = this.activeEmployees.filter(e => memberIDs.includes(e.EmployeeID));
      } else {
        this.manageMembersList = [];
      }
      this.showManageMembersModal = true;
    },

    async saveBoardMembership() {
      if (!this.currentBoard || !this.currentBoard.id) return;
      try {
        const members = this.manageMembersIsPrivate
          ? this.manageMembersList.map(e => e.EmployeeID)
          : [];
        await this.updateBoardMembership({ boardId: this.currentBoard.id, members });
        this.$toast.success(this.manageMembersIsPrivate
          ? 'Board is now private to the selected employees.'
          : 'Board is now visible to everyone at the site.');
        this.showManageMembersModal = false;
      } catch (err) {
        this.$toast.error('Could not update membership. Please try again.');
        console.error(err);
      }
    },

    resetBoardModalFields() {
      this.boardName = '';
      this.boardDescription = '';
      this.selectedTemplate = null;
      this.newBoardIsPrivate = false;
      this.newBoardMembers = [];
    },

    // Open the Tag Alerts modal pre-filled from the current board's config.
    openTagAlertsModal() {
      if (!this.currentBoard) return;
      const cfg = this.currentBoard.tagAlerts || {};
      this.tagAlertsEnabled = !!cfg.enabled;
      this.tagAlertsTags = Array.isArray(cfg.tags) ? cfg.tags.map(name => ({ name })) : [];
      const recipIDs = Array.isArray(cfg.recipientEmployeeIDs) ? cfg.recipientEmployeeIDs : [];
      // Only surface recipients that are still eligible for this board's
      // visibility (a member removed since config-time drops out of the list).
      this.tagAlertsRecipients = this.tagAlertRecipientOptions.filter(e => recipIDs.includes(e.EmployeeID));
      this.tagAlertsPendingTag = '';
      this.showTagAlertsModal = true;
    },

    // Track what the user has TYPED into the alert-tags multiselect but not yet
    // committed with Enter. vue-multiselect holds this only in its own internal
    // search state, so without this the text vanishes when the user clicks Save.
    //
    // BLANK EMITS ARE IGNORED ON PURPOSE. vue-multiselect's deactivate() does
    // `if (!this.preserveSearch) this.search = ''` (multiselectMixin.js:696) and
    // the `search` watcher then emits search-change('') — and this widget runs
    // :preserve-search="false". Clicking Save BLURS the box, so honoring that
    // blank would wipe the very text we are trying to rescue, re-creating the
    // original bug. Whether the blank lands before or after the click handler is
    // a Vue flush-timing detail we refuse to depend on, so the buffer is only
    // ever cleared explicitly: on commit (onAddAlertTag), on modal open, and on
    // modal reset.
    //
    // Trade-off: a user who types a tag and then DELETES it before saving still
    // gets it saved. That is visible and trivially removable — the opposite
    // failure (silently saving an alert that can never fire) is the one that
    // reached a customer.
    onAlertTagSearchChange(query) {
      const q = typeof query === 'string' ? query : '';
      if (!q.trim()) return;
      this.tagAlertsPendingTag = q;
    },

    // Add-tag handler for the alert-tags multiselect. Enforces the caps
    // (≤10 tags, ≤40 chars each) and case-insensitive de-dupe at input time.
    onAddAlertTag(newTag) {
      if (typeof newTag !== 'string') return;
      const clean = newTag.trim();
      if (!clean) return;
      if (this.tagAlertsTags.length >= 10) {
        this.$toast.warning('You can add up to 10 alert tags.');
        return;
      }
      if (clean.length > 40) {
        this.$toast.warning('Tags must be 40 characters or fewer.');
        return;
      }
      const exists = this.tagAlertsTags.some(t => t.name && t.name.toLowerCase() === clean.toLowerCase());
      if (!exists) this.tagAlertsTags.push({ name: clean });
      // Committed — it must not be re-added by the pending-text commit in save.
      this.tagAlertsPendingTag = '';
    },

    async saveTagAlerts() {
      if (!this.currentBoard || !this.currentBoard.id) return;
      // Belt-and-suspenders: the form is only rendered when emailEnabled, so
      // Save is unreachable otherwise — but never persist a config for a site
      // that can't send email.
      if (!this.emailEnabled) return;
      try {
        // Normalize tags to canonical lowercase + cap at 10; cap recipients at 20.
        // The recipients picker enforces :max="20" at input time, so this slice
        // is defensive — but if it ever truncates, tell the user rather than
        // silently dropping people they picked.
        if (this.tagAlertsEnabled && this.tagAlertsRecipients.length > 20) {
          this.$toast.warning('Only the first 20 people will be alerted.');
        }
        // Commit a tag the user TYPED but never pressed Enter on. vue-multiselect
        // only emits @tag on Enter, so without this the text is silently dropped
        // and we persist an alert that can never fire (the Allay incident,
        // 2026-08-19: enabled:true, tags:[], and a "saved" toast).
        if (this.tagAlertsEnabled && this.tagAlertsPendingTag.trim()) {
          this.onAddAlertTag(this.tagAlertsPendingTag);
        }
        const tags = normalizeTagNames(this.tagAlertsTags).slice(0, 10);
        const recipientEmployeeIDs = this.tagAlertsRecipients
          .map(e => e.EmployeeID)
          .slice(0, 20);
        // INERT-CONFIG GUARD. An enabled alert with no trigger tag — or nobody to
        // email — can never send anything. Refuse the save and say why, instead of
        // storing it and reporting success (which is what made the original bug
        // invisible to the customer for two days).
        if (this.tagAlertsEnabled && tags.length === 0) {
          this.$toast.warning('Add at least one tag that triggers the alert, then press Enter.');
          return;
        }
        if (this.tagAlertsEnabled && recipientEmployeeIDs.length === 0) {
          this.$toast.warning('Add at least one person to email.');
          return;
        }
        const tagAlerts = {
          enabled: !!this.tagAlertsEnabled,
          // When disabled, clear the payload so a re-enable starts intentional.
          tags: this.tagAlertsEnabled ? tags : [],
          recipientEmployeeIDs: this.tagAlertsEnabled ? recipientEmployeeIDs : [],
        };
        await this.updateTagAlerts({ boardId: this.currentBoard.id, tagAlerts });
        // Reflect locally so the card panels' fire-hook sees the new config
        // immediately; the Firestore listener will also refresh it shortly.
        // Re-check currentBoard: the fbboards watcher can null it out mid-save
        // (board evicted / user removed). The write already SUCCEEDED, so a null
        // here must not throw into the catch and show a false "couldn't save".
        if (this.currentBoard) { Vue.set(this.currentBoard, 'tagAlerts', tagAlerts); }
        this.$toast.success(tagAlerts.enabled
          ? 'Tag alerts saved.'
          : 'Tag alerts turned off.');
        this.showTagAlertsModal = false;
      } catch (err) {
        this.$toast.error('Could not save tag alerts. Please try again.');
        console.error(err);
      }
    },

    showAddColumnModal() {
      this.newColumn = {
        name: '',
        tasks: []
      }
      this.showColumnModal = true;
    },

    openEditColumnModal(column, index) {
      this.editColumnName = column.name;
      this.editColumnIndex = index;
      this.showEditColumnModal = true;
    },

    runSureOk(confirmFor) {
      switch(confirmFor) {
          case 'remove-column': this.removeColumn(this.selectedColumnIdx); break;                                                    
          case 'remove-task': this.removeTask(this.selectedColumnIdx, this.selectedTaskIdx); break;
          case 'remove-board': this.removeBoard(this.selectedBoardId); break;
          default: break;
      }
    },

    async removeTask(columnIdx, taskIdx) {
      try {
        // Remove the task from the current board
        const updatedColumns = [...this.currentBoard.columns];
        updatedColumns[columnIdx].tasks.splice(taskIdx, 1);

        // Prepare the payload to update the board
        const payload = {
          boardId: this.currentBoard.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        // Dispatch the update action
        await this.$store.dispatch('kanban/updateBoard', payload);

        // Show success message
        this.$toast.success("Task deleted successfully!");
      } catch (error) {
        this.$toast.error("Error deleting task. Please try again.");
      }
    },

    async removeColumn(selectedColumnIdx) {
      this.currentBoard.columns.splice(selectedColumnIdx, 1);
      let payload = {            
        boardId: this.currentBoard.id,
        updates: {
          columns: this.currentBoard.columns,
          lastModified: new Date(),
          lastModifiedBy: this.userID
        }
      };

      await this.$store.dispatch('kanban/updateBoard', payload);
    },

    async removeBoard(boardId) {
      try {
        await this.$store.dispatch('kanban/deleteBoard', { boardId: boardId });
        Vue.set(this, 'selectedBoard', null);
        this.selectedBoardId = null;
        this.currentBoard = null;
      } catch (error) {
        this.$toast.error("Error deleting board. Please try again.");
      }
    },

    // Called from the 3-dot menu on a dashboard board-list item.
    // Sets selectedBoardId so runSureOk('remove-board') knows which board
    // to delete, then opens the same confirm modal used elsewhere.
    confirmDeleteBoardFromList(board) {
      if (!board || !board.id) return;
      this.selectedBoardId = board.id;
      this.$refs['sure-modal-remove-board'].show();
    },

    async handleAddColumn(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (!this.columnName) return;  
      try {
        await this.addColumn(
            { 
              boardId: this.currentBoard.id, 
              column: { 
                id: Date.now().toString(), 
                name: this.columnName, 
                tasks: [] 
              } 
          });
          this.columnName = ''; 
          this.$nextTick(() => {
            this.showColumnModal = false
          }
        );
      } catch (err) {
        this.$toast.error("Error adding a column. Please try again");
        console.log(err);
      }
    },

    async handleEditColumn() {
      if (!this.editColumnName) {
        this.$toast.error("Column name is required. Please try again");
        return;
      }
      
      try {
        // Update the column name in the current board
        const updatedColumns = [...this.currentBoard.columns];
        updatedColumns[this.editColumnIndex].name = this.editColumnName;

        // Prepare the payload to update the board
        const payload = {
          boardId: this.currentBoard.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        // Dispatch the update action
        await this.$store.dispatch('kanban/updateBoard', payload);

        // Reset the edit data
        this.editColumnName = '';
        this.editColumnIndex = null;
        this.showEditColumnModal = false;

        // Show success message
        this.$toast.success("Column updated successfully!");

      } catch (error) {
        this.$toast.error("Error updating column. Please try again.");
      }
    },

    async handleColumnMove(e) {
      if (!e.moved) return; // Only handle reordering within the same board
      
      try {
        // Update the board with the new column order
        const payload = {
          boardId: this.currentBoard.id,
          updates: {
            columns: this.currentBoard.columns, // The draggable component already updated the array
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        await this.$store.dispatch('kanban/updateBoard', payload);
        this.$toast.success('Column order updated!');

      } catch (error) {
        this.$toast.error('Error reordering columns. Please try again.');
        console.error('Column reorder error:', error);
      }
    },

    async handleTaskMove(e, column, columnIdx) { 
      if (!e.added && !e.removed && !e.moved) return;
      
      try {
        const fromColumnIdx = e.removed ? e.removed.newIndex : undefined;
        const toColumnIdx = e.added ? e.added.newIndex : undefined;
        const taskId = (e.added || e.removed || e.moved).element.id;

        const updatedColumns = [ ...this.currentBoard.columns ];

      
        if (fromColumnIdx) {
          updatedColumns[fromColumnIdx] = {
            ...updatedColumns[fromColumnIdx],
            tasks: updatedColumns[fromColumnIdx].tasks.filter(task => task.id !== taskId)
          }
        }
        
        if (toColumnIdx) {
          updatedColumns[toColumnIdx] = {
            ...updatedColumns[toColumnIdx],
            tasks: [...updatedColumns[toColumnIdx].tasks]
          }
          updatedColumns[toColumnIdx].tasks.splice(e.added.newIndex, 0, e.added.element);
        }

        // Handle reordering within same column
        if (e.moved) {

          updatedColumns[columnIdx].tasks = this.currentBoard.columns[columnIdx].tasks;
        }


        let payload = {            
          boardId: this.currentBoard.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        await this.$store.dispatch('kanban/updateBoard', payload);

      } catch (error) {
        this.$toast.error("Error moving task. Please try again.");
      }
      
    },

    loadBoards() {
      try {
        this.$store.dispatch('kanban/fetchBoards', {siteID: this.siteID})
          .then(() => {
            setTimeout(() => {
              this.busy = false;
            }, 1000);
          })
          .catch(error => {
            this.busy = false;
            this.$toast.error('Error loading boards. Please try again');
          });
      } catch (error) {
        this.busy = false;
        this.$toast.error('Error loading boards. Please try again');
      }
    },

    async handleMoveTask(moveData) {
      const { task, fromColumnIdx, taskIdx, targetColumn } = moveData;
      
      try {
        // Find target column index
        const targetColumnIdx = this.currentBoard.columns.findIndex(col => col.id === targetColumn.id);
        
        if (targetColumnIdx === -1) {
          this.$toast.error('Target column not found');
          return;
        }

        // Create updated columns array
        const updatedColumns = [...this.currentBoard.columns];
        
        // Remove task from source column
        updatedColumns[fromColumnIdx].tasks.splice(taskIdx, 1);
        
        // Add task to target column
        updatedColumns[targetColumnIdx].tasks.push(task);

        // Update the board
        const payload = {
          boardId: this.currentBoard.id,
          updates: {
            columns: updatedColumns,
            lastModified: new Date(),
            lastModifiedBy: this.userID
          }
        };

        await this.$store.dispatch('kanban/updateBoard', payload);
        this.$toast.success(`Task moved to ${targetColumn.name}!`);

      } catch (error) {
        this.$toast.error('Error moving task. Please try again.');
      }
    },

  },

  watch: {
    // Rebuild the boards listener once the viewer's EmployeeID
    // (customer.userID) hydrates. fetchBoards refuses to build the listener
    // with an empty userID, because array-contains-any ['', '*'] silently
    // drops every PRIVATE board the user is a member of while still returning
    // public ones (see store/kanban.js). Without this rebuild, a user whose
    // userID loads after Kanban mounts would be stuck seeing only public
    // boards for the whole session. (Tanya Rajdev, 2026-06-03.)
    userID(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadBoards();
      }
    },

    selectedBoard: {
      handler(newBoard) {
        if (newBoard) {
          this.currentBoard = this.getBoard(newBoard.value);
          this.selectedBoardId = newBoard.value;
          
          // Auto-select first column for mobile view
          if (this.currentBoard && this.currentBoard.columns && this.currentBoard.columns.length > 0) {
            this.selectedColumn = this.currentBoard.columns[0];
          }
        }
      }
    },

    currentBoard: {
      handler(newBoard) {
        // Ensure selectedColumn is valid for the current board.
        // NOTE: intentionally NOT deep — this handler only cares about the
        // top-level columns array identity. deep:true was traversing the
        // entire nested board tree on every change (hundreds of tasks/tags),
        // contributing to JS-thread freezes when switching boards.
        if (newBoard && newBoard.columns) {
          if (!this.selectedColumn || !newBoard.columns.find(col => col.id === this.selectedColumn.id)) {
            this.selectedColumn = newBoard.columns.length > 0 ? newBoard.columns[0] : null;
          }
        }
      }
    },

    fbboards: {
      // NOTE: intentionally NOT deep. This watcher only re-resolves
      // currentBoard by ID when the boards array is replaced by the
      // Firestore snapshot (store debounces snapshots 250ms). Deep traversal
      // of all boards + all columns + all tasks on every update was an
      // O(board × column × task) CPU cost that contributed to JS-thread
      // freezes when switching boards. Array-identity change is enough.
      handler(newValue) {
        // Eviction case: the user is currently viewing a board that the
        // listener no longer returns (they got removed from the member
        // list, or the board was deleted by someone else). Reset to the
        // dashboard with a toast so they're not stuck on a stale view.
        if (this.selectedBoardId && Array.isArray(newValue) && !this.getBoard(this.selectedBoardId)) {
          Vue.set(this, 'selectedBoard', null);
          Vue.set(this, 'selectedBoardId', null);
          Vue.set(this, 'currentBoard', null);
          if (this.$toast && typeof this.$toast.info === 'function') {
            this.$toast.info("You no longer have access to that board.");
          }
          return;
        }
        if (!_.isEmpty(newValue)) {
          const board = this.getBoard(this.selectedBoardId);
          if (board) {
            Vue.set(this, 'currentBoard', board);

            if (this.selectedColumn) {
              const tempIndex = board.columns.findIndex(c => c.id === this.selectedColumn.id);
              if (tempIndex >= 0) {
                Vue.set(this, 'selectedColumn', board.columns[tempIndex]);
              } else {
                Vue.set(this, 'selectedColumn', board.columns.length > 0 ? board.columns[0] : null);
              }
            }
          }
        }
      }
    }
  },
}
</script>

<style>
/* Non-scoped CSS variables so they work globally */
:root {
  --primary-color: #3abf94;
  --primary-dark: #2d9474;
  --primary-light: #e6f7f1;
  --secondary-color: #4fd1a5;
  --accent-color: #2d9474;
  --success-color: #22c55e;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --light-bg: #f8fafc;
  --medium-bg: #f1f5f9;
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #94a3b8;
  --border-color: #e2e8f0;
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --transition: all 0.2s ease-in-out;
}

/* ===== Non-scoped overrides for third-party components ===== */

/* Fix: Board selector multiselect dropdown */
.board-selector-wrapper .multiselect {
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif !important;
  min-height: 42px !important;
  background-color: #ffffff !important;
}

.board-selector-wrapper .multiselect__tags {
  border-radius: 6px !important;
  border: none !important;
  background-color: #ffffff !important;
  padding: 8px 40px 8px 12px !important;
  min-height: 42px !important;
  display: flex !important;
  align-items: center !important;
}

.board-selector-wrapper .multiselect__single {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #0f172a !important;
  margin-bottom: 0 !important;
  padding: 0 !important;
  background-color: transparent !important;
  line-height: 1.4 !important;
}

.board-selector-wrapper .multiselect__placeholder {
  color: #94a3b8 !important;
  font-size: 14px !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

.board-selector-wrapper .multiselect__content-wrapper {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  margin-top: 4px !important;
  background-color: #ffffff !important;
  max-height: 300px !important;
}

.board-selector-wrapper .multiselect__option {
  padding: 10px 12px !important;
  font-size: 14px !important;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif !important;
}

.board-selector-wrapper .multiselect__option--highlight {
  background: #3abf94 !important;
  color: white !important;
}

.board-selector-wrapper .multiselect__select {
  height: 42px !important;
  width: 36px !important;
}

/* Buttons use global Bootstrap theming from _variables.scss (success: $teal #3abf94) */

/* Fix: Search input visibility */
.board-search-box {
  background-color: #ffffff !important;
}

.board-search-input {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  color: #0f172a !important;
}

.board-search-input:focus {
  background-color: #ffffff !important;
  border-color: #3abf94 !important;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.15) !important;
}
</style>

<style scoped>
/* ===== Kanban Styles ===== */

/* Settings gear button — same visual language as Tasks / Chat / SMS gears. */
.kanban-settings-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  background: white;
  border: 1px solid #e2e8f0;
  color: #718096;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.kanban-settings-btn:hover {
  background: #f7fafc;
  color: #3abf94;
  border-color: #3abf94;
}

.kanban-settings-btn .fa {
  font-size: 14px;
  transition: transform 0.3s ease;
}

.kanban-settings-btn:hover .fa {
  transform: rotate(45deg);
}

/* Contenteditable details editor — mirrors the look of b-form-textarea so the
   visual change vs the old <b-form-textarea> is minimal. */
.kanban-details-editor {
  min-height: 72px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
}

.kanban-details-editor:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.kanban-details-editor:empty:before {
  content: attr(placeholder);
  color: #adb5bd;
  pointer-events: none;
}

/* Base Layout - Full viewport height */
.kanban-board-container {
  height: calc(100vh - 200px); /* Adjust based on header/navbar height */
  min-height: 600px;
}

.kanban-container {
  height: 100%;
  padding: 0.5rem 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: #c1c9d2 #f8f9fa;
  overflow-x: auto;
  overflow-y: hidden;
}

/* Custom scrollbar styling - consistent with Workflow */
.kanban-container::-webkit-scrollbar {
  height: 8px;
}

.kanban-container::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 4px;
}

.kanban-container::-webkit-scrollbar-thumb {
  background-color: #c1c9d2;
  border-radius: 4px;
  opacity: 0.7;
}

.kanban-container::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}

/* Column styling - Matching TaskManager task-section style */
.kanban-column {
  min-width: 320px;
  max-width: 320px;
  margin-right: 1.25rem;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  background-color: white;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* Removed top gradient bar for cleaner look */

.kanban-column:hover {
  box-shadow: var(--shadow-md);
  border-color: #cbd5e1;
}

.kanban-column .card-body {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.column-header {
  flex-shrink: 0;
  padding: 16px 16px 12px 16px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
}

.column-actions {
  display: flex;
  gap: 0.25rem;
  opacity: 0.5;
  transition: var(--transition);
}

.kanban-column:hover .column-actions {
  opacity: 1;
}

.column-tasks-container {
  flex: 1; /* Take remaining space */
  min-height: 0; /* Allow flexbox to work properly */
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25rem;
}

/* Hide scrollbar by default, only show when needed and on hover */
.column-tasks-container::-webkit-scrollbar {
  width: 4px;
  background: transparent;
}

.column-tasks-container::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 2px;
}

.column-tasks-container::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 2px;
}

/* Show scrollbar only on hover when there's overflow */
.kanban-column:hover .column-tasks-container::-webkit-scrollbar-thumb {
  background-color: #c1c9d2;
}

/* Firefox scrollbar - hide by default */
.column-tasks-container {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.kanban-column:hover .column-tasks-container {
  scrollbar-color: #c1c9d2 transparent;
}

/* Column title styling - matching TaskManager task-header */
.kanban-column .card-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  margin: 0;
  display: flex;
  align-items: center;
}

.kanban-column .card-title .text-muted {
  font-weight: 500;
  font-size: 13px;
  color: var(--text-muted) !important;
  background-color: var(--medium-bg);
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
}

/* Board title styling - matching TaskManager app-title */
.board-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0;
  letter-spacing: -0.5px;
}

/* Button styling - matching TaskManager design */
.btn-sm {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  font-size: 13px;
  border-radius: var(--radius-sm);
  padding: 0.5rem 0.875rem;
  transition: var(--transition);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* .btn-success uses global Bootstrap theming (success: $teal #3abf94) */

.btn-danger {
  color: #FFF;
  background-color: var(--danger-color);
  border: none;
}

.btn-danger:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.btn-link {
  color: var(--text-muted);
  transition: var(--transition);
  padding: 0.375rem;
}

.btn-link:hover {
  color: var(--primary-color);
  text-decoration: none;
  background-color: var(--primary-light);
  border-radius: var(--radius-sm);
}

.btn-outline-secondary {
  border-color: var(--border-color);
  color: var(--text-secondary);
  background-color: white;
}

.btn-outline-secondary:hover {
  background-color: var(--light-bg);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

/* Icon styling */
.fa {
  transition: transform 0.2s ease;
}

.btn:hover .fa {
  transform: scale(1.1);
}

/* Draggable item styling */
.cursor-grab {
  cursor: grab;
}

.cursor-grab:active {
  cursor: grabbing;
}

.ghost-card {
  opacity: 0.5;
  background: #f8f9fa;
  border: 2px dashed #6c757d;
}

/* Column dragging styles */
.column-drag-container {
  width: 100%;
}

.ghost-column {
  opacity: 0.3;
  background: #e9ecef;
  border: 2px dashed #007bff;
  transform: rotate(2deg);
}

.drag-column {
  transform: rotate(5deg);
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.column-drag-handle {
  cursor: grab;
  transition: all 0.2s ease;
  opacity: 0.7;
  font-size: 16px;
  min-width: 20px;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.column-drag-handle:hover {
  opacity: 1;
  color: #007bff !important;
  transform: scale(1.1);
}

.column-drag-handle:active {
  cursor: grabbing;
}

/* Expanded drag area - minimal height when empty */
.drag-area-expanded {
  min-height: 50px;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

/* When dragging over a valid drop zone */
.drag-area-expanded:hover {
  background-color: rgba(40, 167, 69, 0.05);
}

/* Style for when something is being dragged over this area */
.drag-area-expanded.sortable-drag-over {
  background-color: rgba(40, 167, 69, 0.1) !important;
  border: 2px dashed #28a745 !important;
  animation: pulse-drop-zone 1s ease-in-out infinite;
}

@keyframes pulse-drop-zone {
  0%, 100% {
    border-color: #28a745;
    background-color: rgba(40, 167, 69, 0.1);
  }
  50% {
    border-color: #20c997;
    background-color: rgba(40, 167, 69, 0.15);
  }
}

/* Empty state - compact, centered at top of column */
.empty-column-state {
  text-align: center;
  padding: 1rem 0.75rem;
  color: var(--text-muted);
  background-color: var(--light-bg);
  border-radius: var(--radius-md);
  border: 1px dashed var(--border-color);
  margin: 0.25rem;
}

.empty-column-state .fa {
  font-size: 1rem;
  margin-bottom: 0.25rem;
  display: block;
  opacity: 0.4;
}

.empty-column-state p {
  font-size: 11px;
  margin: 0;
}

/* Hide column drag handle on mobile since column reordering is not available in mobile view */
@media (max-width: 767.98px) {
  .column-drag-handle {
    display: none;
  }
}

/* Industry-standard grip icon - two columns of dots */
.grip-dots {
  width: 12px;
  height: 16px;
  position: relative;
  display: inline-block;
}

.grip-dots::before,
.grip-dots::after {
  content: '';
  position: absolute;
  width: 3px;
  height: 3px;
  background-color: currentColor;
  border-radius: 50%;
  opacity: 0.6;
}

/* Left column of dots */
.grip-dots::before {
  left: 0;
  box-shadow: 
    0 0 0 0 currentColor,
    0 5px 0 0 currentColor,
    0 10px 0 0 currentColor;
}

/* Right column of dots */
.grip-dots::after {
  right: 0;
  box-shadow: 
    0 0 0 0 currentColor,
    0 5px 0 0 currentColor,
    0 10px 0 0 currentColor;
}

/* Professional drag handle styling */
.column-drag-handle {
  background-color: rgba(108, 117, 125, 0.1);
  border-radius: 4px;
  padding: 4px 6px;
  transition: all 0.2s ease;
}

.column-drag-handle:hover {
  background-color: rgba(0, 123, 255, 0.15);
}

.column-drag-handle:hover .grip-dots::before,
.column-drag-handle:hover .grip-dots::after {
  opacity: 1;
}

/* Mobile responsiveness */
@media (max-width: 991.98px) and (min-width: 768px) {
  /* Tablet responsiveness */
  .kanban-column {
    min-width: 280px;
    max-width: 280px;
    margin-right: 1rem;
  }
  
  .kanban-container {
    padding: 0.5rem 0.125rem;
  }
  
  .kanban-board-container {
    height: calc(100vh - 180px);
  }
}

@media (max-width: 767.98px) {
  /* Mobile kanban container */
  .mobile-kanban-container {
    height: calc(100vh - 220px);
    min-height: 500px;
  }
  
  .kanban-column {
    min-width: 100%;
    max-width: 100%;
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .board-title {
    font-size: 18px;
  }
  
  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
  
  /* Mobile-specific column select */
  .mobile-column-select {
    font-size: 16px;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    border: 2px solid #E8E8EF;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(34, 34, 87, 0.05);
  }
  
  .mobile-column-select:focus {
    border-color: #217CE8;
    box-shadow: 0 0 0 3px rgba(33, 124, 232, 0.15);
  }
  
  /* Mobile column card */
  .mobile-column-card {
    background-color: #fff;
    border: 1px solid #E8E8EF;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(34, 34, 87, 0.08);
  }
  
  .mobile-column-title {
    font-size: 18px;
    color: #393C44;
    font-weight: 600;
  }
  
  .task-count-badge {
    font-size: 14px;
    color: #808292;
    font-weight: 500;
  }
  
  .mobile-column-actions {
    flex-shrink: 0;
  }
  
  .mobile-add-btn {
    font-size: 14px;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    white-space: nowrap;
  }
  
  .mobile-tasks-container {
    min-height: 200px;
  }
  
  .empty-column-mobile {
    text-align: center;
    padding: 3rem 1rem;
    border: 2px dashed #E8E8EF;
    border-radius: 8px;
    background-color: #f8f9fc;
  }
  
  .mobile-no-column-selected {
    background-color: #f8f9fc;
    border-radius: 12px;
    border: 1px solid #E8E8EF;
  }
  
  /* Improve button spacing on mobile */
  .btn-sm.mt-4.mx-2.mb-2 {
    margin: 0.5rem 0 !important;
    width: 100%;
    justify-content: center;
  }
  
  /* Make multiselect mobile friendly */
  .multiselect {
    min-height: 48px;
  }
  
  .multiselect__tags {
    min-height: 48px;
    padding: 12px 40px 0 12px;
  }
  
  .multiselect__input {
    font-size: 16px;
  }
}

@media (max-width: 575.98px) {
  .kanban-container {
    padding: 0.25rem 0.1rem 1rem;
  }
  
  .board-title {
    font-size: 16px;
  }
  
  .mobile-column-title {
    font-size: 16px;
  }
  
  .mobile-add-btn {
    font-size: 13px;
    padding: 0.4rem 0.6rem;
  }
  
  .mobile-add-btn .fa {
    display: none;
  }
  
  .task-count-badge {
    font-size: 13px;
  }
}

/* Board selector styling - matching TaskManager form controls */
.board-selector-wrapper ::v-deep .multiselect {
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  min-height: 42px;
  background-color: #ffffff;
}

.board-selector-wrapper ::v-deep .multiselect:hover {
  box-shadow: var(--shadow-md);
  border-color: #cbd5e1;
}

.board-selector-wrapper ::v-deep .multiselect__tags {
  border-radius: var(--radius-sm);
  border: none;
  background-color: #ffffff;
  padding: 8px 40px 8px 12px;
  min-height: 42px;
  display: flex;
  align-items: center;
}

.board-selector-wrapper ::v-deep .multiselect__input {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  color: var(--text-primary);
  background-color: transparent;
  margin-bottom: 0;
  padding: 0;
}

.board-selector-wrapper ::v-deep .multiselect__single {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0;
  padding: 0;
  background-color: transparent;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.board-selector-wrapper ::v-deep .multiselect__placeholder {
  color: var(--text-muted);
  font-size: 14px;
  margin-bottom: 0;
  padding-top: 0;
}

.board-selector-wrapper ::v-deep .multiselect__option {
  padding: 10px 12px;
  font-size: 14px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
}

.board-selector-wrapper ::v-deep .multiselect__option--highlight {
  background: var(--primary-color);
  color: white;
}

.board-selector-wrapper ::v-deep .multiselect__option--selected {
  background: var(--primary-light);
  color: var(--primary-dark);
  font-weight: 500;
}

.board-selector-wrapper ::v-deep .multiselect__content-wrapper {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  margin-top: 4px;
  background-color: #ffffff;
  max-height: 300px;
}

.board-selector-wrapper ::v-deep .multiselect__select {
  height: 42px;
  width: 36px;
  background-color: transparent;
}

.board-selector-wrapper ::v-deep .multiselect__select::before {
  border-color: var(--text-muted) transparent transparent;
}

.board-selector-wrapper ::v-deep .multiselect--active {
  z-index: 1050;
}

.board-selector-wrapper ::v-deep .multiselect__element {
  margin-bottom: 0;
}

/* Helpers */
.no-wrap {
  white-space: nowrap;
}

/* Touch-friendly improvements */
@media (max-width: 767.98px) {
  .btn, .btn-sm, .btn-link {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: manipulation;
  }
  
  .form-control, .form-select, .multiselect {
    min-height: 44px;
    font-size: 16px; /* Prevents zoom on iOS */
  }
}

/* Modal styling - matching TaskManager design */
::v-deep .modal-content {
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

::v-deep .modal-header {
  background-color: white;
  border-bottom: 1px solid var(--border-color);
  padding: 20px 24px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
}

::v-deep .modal-title {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 18px;
  letter-spacing: -0.3px;
}

::v-deep .modal-body {
  padding: 24px;
  background-color: white;
}

::v-deep .modal-footer {
  border-top: 1px solid var(--border-color);
  padding: 16px 24px;
  background-color: var(--light-bg);
}

/* Form control styling - matching TaskManager */
::v-deep .form-control {
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  padding: 12px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  transition: var(--transition);
  height: auto;
}

::v-deep .form-control:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.15);
}

::v-deep .form-label {
  font-weight: 500;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

/* Mobile modal improvements */
@media (max-width: 767.98px) {
  ::v-deep .modal-dialog {
    margin: 1rem;
    max-width: calc(100% - 2rem);
  }
  
  ::v-deep .modal-content {
    border-radius: 12px;
  }
  
  ::v-deep .modal-header {
    padding: 1.25rem 1.5rem 1rem;
  }
  
  ::v-deep .modal-body {
    padding: 1rem 1.5rem 1.25rem;
  }
  
  ::v-deep .modal-footer {
    padding: 1rem 1.5rem 1.25rem;
    flex-direction: column-reverse;
  }
  
  ::v-deep .modal-footer .btn {
    width: 100%;
    margin: 0.25rem 0 !important;
  }
  
  ::v-deep .form-control,
  ::v-deep .form-control:focus {
    font-size: 16px;
    min-height: 48px;
  }
  
  ::v-deep .multiselect {
    font-size: 16px;
  }
  
  ::v-deep .multiselect__tags {
    min-height: 48px;
    padding: 12px 40px 0 12px;
  }
}

/* Empty state styling - matching TaskManager empty-state */
.empty-state-card {
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  max-width: 900px;
  transition: var(--transition);
  background-color: white;
}

.empty-state-card:hover {
  box-shadow: var(--shadow-lg);
}

.empty-state-image {
  border-radius: var(--radius-md);
  max-height: 300px;
  object-fit: cover;
}

.empty-state-content {
  background-color: white;
  padding: 2rem;
}

.empty-state-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 22px;
  margin-bottom: 1rem;
  letter-spacing: -0.3px;
}

.empty-state-description {
  color: var(--text-secondary);
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.create-board-btn {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  background-color: var(--primary-color);
  border-radius: var(--radius-sm);
  padding: 12px 20px;
  border: none;
  transition: var(--transition);
  box-shadow: 0 2px 5px rgba(58, 191, 148, 0.2);
}

.create-board-btn:hover {
  background-color: var(--primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(58, 191, 148, 0.3);
  cursor: pointer;
}

/* Responsive adjustments for empty state */
@media (max-width: 767.98px) {
  .empty-state-card {
    max-width: 95%;
    margin: 0 auto;
  }
  
  .empty-state-title {
    font-size: 1.5rem;
  }
  
  .empty-state-description {
    font-size: 1rem;
  }
  
  .create-board-btn {
    padding: 0.5rem 1rem;
  }
}

/* Add keyframes for spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== New Empty State with HTML/CSS Illustration ===== */
.kanban-empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 2rem;
}

.kanban-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 800px;
}

.kanban-explainer {
  max-width: 500px;
  margin-bottom: 24px;
  text-align: center;
}

.kanban-explainer .explainer-title {
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.kanban-explainer .explainer-title i {
  color: #3abf94;
  font-size: 20px;
}

.kanban-explainer .explainer-description {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.kanban-explainer .explainer-description strong {
  color: #475569;
  font-weight: 600;
}

.kanban-explainer .explainer-examples {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #e2e8f0;
}

.kanban-explainer .example-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #475569;
  text-align: left;
}

.kanban-explainer .example-item i {
  width: 20px;
  color: #3abf94;
  font-size: 14px;
  flex-shrink: 0;
}

/* ===== Board Header Styles - Matching TaskManager ===== */
.board-header-row {
  background: white;
  padding: 16px 20px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  margin-bottom: 24px;
}

.back-btn {
  border-color: var(--border-color);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  padding: 8px 14px;
  border-radius: var(--radius-sm);
  transition: var(--transition);
  background-color: white;
}

.back-btn:hover {
  background-color: var(--light-bg);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.board-selector-wrapper {
  min-width: 220px;
  max-width: 300px;
}

/* "or" divider between the board selector and New Board button so users
   don't think they must pick an existing board before creating a new one. */
.board-or-divider {
  display: inline-block;
  margin: 0 10px;
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: lowercase;
  letter-spacing: 0.5px;
}

.board-search-wrapper {
  flex: 1;
  max-width: 320px;
  min-width: 200px;
}

.board-search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.board-search-input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 14px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  transition: var(--transition);
  background-color: #ffffff;
  color: var(--text-primary);
}

.board-search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.15);
}

.board-search-input::placeholder {
  color: var(--text-muted);
}

.board-search-box {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border-radius: var(--radius-md);
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--text-muted);
  font-size: 14px;
  pointer-events: none;
}

.search-clear-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: var(--transition);
}

.search-clear-btn:hover {
  color: var(--text-secondary);
  background: var(--medium-bg);
}

.delete-board-btn {
  color: var(--text-muted);
  padding: 8px;
  border-radius: var(--radius-sm);
  transition: var(--transition);
}

.delete-board-btn:hover {
  color: var(--danger-color);
  background-color: #fef2f2;
}

.tag-alerts-btn {
  color: var(--text-muted);
  padding: 8px;
  border-radius: var(--radius-sm);
  transition: var(--transition);
}

.tag-alerts-btn:hover {
  color: var(--primary-color);
  background-color: var(--primary-light);
}

.gap-2 {
  gap: 8px;
}

/* Search results indicator - matching TaskManager style */
.search-results-indicator {
  background: var(--primary-light);
  color: var(--primary-dark);
  padding: 10px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(58, 191, 148, 0.2);
}

.search-results-indicator .clear-link {
  color: var(--primary-color);
  cursor: pointer;
  font-weight: 500;
  transition: var(--transition);
}

.search-results-indicator .clear-link:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

/* Task highlight when searching */
.task-hidden {
  display: none !important;
}

.task-highlight {
  animation: highlightPulse 0.5s ease-out;
  box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.4) !important;
  border-radius: var(--radius-md);
}

@keyframes highlightPulse {
  0% {
    box-shadow: 0 0 0 4px rgba(58, 191, 148, 0.5);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.4);
  }
}

/* ===== Dashboard Styles - Matching TaskManager ===== */
.kanban-dashboard-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: calc(100vh - 250px);
  padding: 2rem 1rem;
  background-color: var(--light-bg);
}

.kanban-dashboard {
  width: 100%;
  max-width: 900px;
}

/* Hero Section */
.kanban-hero {
  text-align: center;
  margin-bottom: 2rem;
}

.kanban-hero-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -0.5px;
}

.kanban-hero-subtitle {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0;
}

/* Section Headers */
.section-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-title i {
  color: var(--text-muted);
  opacity: 0.7;
}

/* Compact Board List - Matching TaskManager card styling */
.board-list {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.board-list-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: var(--transition);
  border-bottom: 1px solid var(--border-color);
}

.board-list-item:last-child {
  border-bottom: none;
}

.board-list-item:hover {
  background-color: var(--light-bg);
}

.board-list-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: var(--primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
  transition: var(--transition);
}

.board-list-icon i {
  font-size: 18px;
  color: var(--primary-color);
}

.board-list-item:hover .board-list-icon {
  background: var(--primary-color);
}

.board-list-item:hover .board-list-icon i {
  color: white;
}

.board-list-icon.new {
  background: rgba(34, 197, 94, 0.1);
}

.board-list-icon.new i {
  color: var(--success-color);
}

.board-list-item:hover .board-list-icon.new {
  background: var(--success-color);
}

.board-list-item:hover .board-list-icon.new i {
  color: white;
}

.board-list-content {
  flex: 1;
  min-width: 0;
}

.board-list-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Private-board padlock shown beside the title in the Recent Boards list, so a
   secured board is recognizable without opening it (mirrors the in-board lock). */
.board-list-lock {
  font-size: 12px;
  color: var(--text-muted);
}

.board-list-meta {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  color: var(--text-muted);
}

.board-list-arrow {
  color: var(--text-muted);
  margin-left: 12px;
  transition: var(--transition);
}

.board-list-arrow i {
  font-size: 14px;
}

.board-list-item:hover .board-list-arrow {
  color: var(--primary-color);
  transform: translateX(4px);
}

/* Plain delete button on each board list item. Replaces chevron-right. */
.board-list-delete-btn {
  flex-shrink: 0;
  margin-left: 12px;
  padding: 6px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;
}

.board-list-delete-btn:hover {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.board-list-delete-btn i {
  font-size: 14px;
}

.board-list-new {
  background-color: var(--light-bg);
}

.board-list-new:hover {
  background-color: rgba(34, 197, 94, 0.05);
}

/* Template Grid in Modal - Matching TaskManager styling */
.templates-in-modal .form-label {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  color: var(--text-primary);
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.template-option {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
  background: white;
}

.template-option:hover {
  border-color: var(--primary-color);
  background-color: var(--primary-light);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.template-option.selected {
  border-color: var(--primary-color);
  background-color: var(--primary-light);
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.15);
}

.template-option-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.template-option-icon i {
  font-size: 18px;
}

.template-option-content {
  display: flex;
  flex-direction: column;
}

.template-option-name {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.template-option-cols {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  color: var(--text-muted);
}

.custom-board-section .form-label {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  color: var(--text-primary);
}

/* Mobile Responsive - Dashboard */
@media (max-width: 767.98px) {
  .kanban-dashboard-wrapper {
    padding: 1rem;
    min-height: auto;
  }

  .kanban-hero-title {
    font-size: 20px;
  }

  .kanban-hero-subtitle {
    font-size: 14px;
  }

  .section-title {
    font-size: 12px;
  }

  .template-grid {
    grid-template-columns: 1fr;
  }

  .board-list-item {
    padding: 14px 16px;
  }

  .board-list-icon {
    width: 38px;
    height: 38px;
  }

  .board-list-icon i {
    font-size: 16px;
  }

  .board-list-title {
    font-size: 14px;
  }

  .board-list-meta {
    font-size: 12px;
  }

  .board-header-row {
    padding: 12px 16px;
    margin-bottom: 16px;
  }

  .board-selector-wrapper {
    min-width: 180px;
    max-width: 100%;
  }

  .board-search-wrapper {
    max-width: 100%;
    min-width: 150px;
  }
}

/* Extra small devices */
@media (max-width: 480px) {
  .board-header-row .d-flex {
    flex-wrap: wrap;
    gap: 8px;
  }

  .board-selector-wrapper {
    flex: 1;
    min-width: 100%;
  }

  .board-search-wrapper {
    flex: 1;
    min-width: 100%;
    margin-left: 0 !important;
    margin-top: 8px;
  }

  .board-actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: 8px;
  }
}
</style>